import React, { useState, useEffect } from "react";
import { requestGetClasses,
  requestAddAttendanceList,
  requestGetAttendanceList,
  requestGetAbsenceList,
  deleteOldAbsences, auth } from "../utils/resource";
import "../styles/AttendanceList.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faHouse,
  faArrowLeft,
  faArrowRight,
  faXmark,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { format, parse, addDays, subDays, isValid } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

const daysOfWeek = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const formatDate = (date) => {
  return isValid(date) ? format(date, "dd-MM-yyyy", { locale: ptBR }) : "";
};

const parseDate = (dateString) => {
  const parsedDate = parse(dateString, "yyyy-MM-dd", new Date(), {
    locale: ptBR,
  });
  return isValid(parsedDate) ? parsedDate : new Date();
};

const AttendanceList = () => {
  const navigate = useNavigate();

  const [attendance, setAttendance] = useState({});
  const [absenceList, setAbsenceList] = useState([]);

  const [myClasses, setMyClasses] = useState([]);
  const [date, setDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(
    daysOfWeek[new Date().getDay()]
  );
  const [isAdding, setIsAdding] = useState(false);
  const [studentClassesCount, setStudentClassesCount] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 5;

  const countClasses = () => {
    const count = {};
    myClasses.forEach((dayEntry) => {
      Object.values(dayEntry.time).forEach((students) => {
        students.forEach((student) => {
          if (count[student.name]) {
            count[student.name]++;
          } else {
            count[student.name] = 1;
          }
        });
      });
    });
    return count;
  };

  function getStudentsByDay(dia) {
    const result = myClasses.find((item) => item.day === dia);
    if (result && result.time) {
      return Object.values(result.time).flat();
    }
    return [];
  }

  

  useEffect(() => {
    if (Object.keys(studentClassesCount).length > 0) {
      deleteOldAbsences(auth?.currentUser?.email);
      requestGetAbsenceList(auth?.currentUser?.email)
        .then((result) => {
          const filteredResult = result.filter((student) =>
            studentClassesCount.hasOwnProperty(student.name)
          );
          setAbsenceList(filteredResult);
        })
        .catch((error) => {
          console.error("Error fetching absence list:", error);
        });
    }
  }, [studentClassesCount]);

  useEffect(() => {
    requestGetClasses(auth?.currentUser?.email, setMyClasses);
  }, []);

  useEffect(() => {
    const total = Math.ceil(
      absenceList.filter((item) => item.name.toLowerCase().includes(searchTerm))
        .length / itemsPerPage
    );
    setTotalPages(total);
  }, [absenceList, searchTerm]);

  useEffect(() => {
    if (isAdding === true) {
      requestGetAttendanceList(
        auth?.currentUser?.email,
        formatDate(date),
        setAttendance
      );
    } else {
      setDate(new Date());
      setSelectedDay(daysOfWeek[new Date().getDay()]);
    }
  }, [isAdding]);

  useEffect(() => {
    if (myClasses.length > 0) {
      setStudentClassesCount(countClasses());
    }
  }, [myClasses]);

  useEffect(() => {
    requestGetAttendanceList(
      auth?.currentUser?.email,
      formatDate(date),
      setAttendance
    );
    const dayOfWeek = daysOfWeek[date.getDay()];
    setSelectedDay(dayOfWeek);
  }, [date]);

  const handleAttendanceChange = (studentName) => {
    setAttendance((prevState) => ({
      ...prevState,
      [studentName]: !prevState[studentName],
    }));
  };

  const handleDateChange = (event) => {
    const newDate = parseDate(event.target.value);
    setDate(newDate);
  };

  const saveAttendance = async () => {
    const formattedDate = formatDate(date);
    const students = getStudentsByDay(selectedDay);
    students.forEach((student) => {
      if (!(student.name in attendance)) {
        attendance[student.name] = false;
      }
    });

    await requestAddAttendanceList(
      auth?.currentUser?.email,
      formattedDate,
      attendance
    );
    await requestGetAbsenceList(auth?.currentUser?.email, setAbsenceList);
    setIsAdding(false);
  };

  const handleCancel = () => {
    navigate("/dashboard");
  };

  const handlePreviousDay = () => {
    setDate(subDays(date, 1));
  };

  const handleNextDay = () => {
    setDate(addDays(date, 1));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredStudents = absenceList
    .filter((item) => item.name.toLowerCase().includes(searchTerm))
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={i === currentPage ? "active" : ""}
          >
            {i}
          </button>
        );
      }
      return pages;
    }

    pages.push(
      <button
        key={1}
        onClick={() => handlePageChange(1)}
        className={1 === currentPage ? "active" : ""}
      >
        1
      </button>
    );

    if (currentPage > 4) {
      pages.push(
        <span key="ellipsis-start" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);

    if (currentPage <= 3) {
      end = 4;
    } else if (currentPage >= totalPages - 2) {
      start = totalPages - 3;
    }

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={i === currentPage ? "active" : ""}
        >
          {i}
        </button>
      );
    }

    if (currentPage < totalPages - 3) {
      pages.push(
        <span key="ellipsis-end" className="pagination-ellipsis">
          ...
        </span>
      );
    }

    pages.push(
      <button
        key={totalPages}
        onClick={() => handlePageChange(totalPages)}
        className={totalPages === currentPage ? "active" : ""}
      >
        {totalPages}
      </button>
    );

    return pages;
  };

  return (
    <div className="attendance-container">
      <main className="attendance-main">
        <div className="button-bar">
          {isAdding ? (
            <>
              <FontAwesomeIcon
                icon={faSave}
                className="icon save-icon"
                onClick={saveAttendance}
                title="Salvar"
              />
              <FontAwesomeIcon
                icon={faXmark}
                className="icon cancel-icon"
                onClick={() => setIsAdding(false)}
                title="Cancelar"
              />
            </>
          ) : (
            <>
              <FontAwesomeIcon
                icon={faPlus}
                className="icon add-icon"
                onClick={() => setIsAdding(true)}
                title="Adicionar Estudante"
              />
              <FontAwesomeIcon
                icon={faHouse}
                className="icon cancel-icon"
                onClick={() => handleCancel()}
                title="Cancelar"
              />
            </>
          )}
        </div>

        {!isAdding && (
          <div className="">
            <input
              type="text"
              placeholder="Buscar por nome"
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
            <table className="student-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Aulas</th>
                  <th>Faltas</th>
                  <th>Datas</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.name}>
                    <td>{student.name}</td>
                    <td>{studentClassesCount[student.name] || 0}</td>
                    <td>{student.absences}</td>
                    <td>
                      {student.timestamps.map((timestamp, index) => (
                        <div key={index}>
                          {format(new Date(timestamp * 1000), "dd-MM-yyyy")}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="pagination">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <div className="pagination-numbers">{renderPageNumbers()}</div>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        )}

        {isAdding && (
          <div>
            <div className="date-selector">
              <button onClick={handlePreviousDay}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
              <label>
                Frequência:
                <input
                  type="date"
                  value={format(date, "yyyy-MM-dd")}
                  onChange={handleDateChange}
                />
              </label>
              <button onClick={handleNextDay}>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>

            <table className="student-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Presente?</th>
                </tr>
              </thead>
              <tbody>
                {getStudentsByDay(selectedDay).map((student) => (
                  <tr key={student.name}>
                    <td>{student.name}</td>
                    <td>
                      <input
                        type="checkbox"
                        checked={attendance[student.name] || false}
                        onChange={() => handleAttendanceChange(student.name)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AttendanceList;
